import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { ModelIcon } from './ModelIcon';
import type { Volume } from '../types/kit';
import { getAccent } from '../utils/accent';
interface VolumeSectionProps {
  volume: Volume;
  reversed: boolean;
}
const VISIBLE_TOPICS = 8;
export function VolumeSection({
  volume,
  reversed
}: VolumeSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const a = getAccent(volume.accent);
  const visible = volume.topics.slice(0, VISIBLE_TOPICS);
  const hidden = volume.topics.slice(VISIBLE_TOPICS);
  return <article id={volume.id} className="scroll-mt-8 border-t border-line py-16 first:border-t-0 lg:py-24" data-id="element-330" data-mp="13qgsvy277wp6">
      <div className={`grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16`} data-id="element-331" data-mp="18mp9lfw1nxyg">
        <div className={`lg:sticky lg:top-8 lg:self-start ${reversed ? 'lg:order-2' : ''}`} data-id="element-332" data-mp="35j4ww9mo2o60">
          <img src={volume.cover} alt={`Portada del Volumen ${volume.number}: ${volume.title}`} className="mx-auto w-full max-w-sm rounded-2xl shadow-[0_30px_50px_-25px_rgba(18,26,21,0.45)]" data-id="element-333" data-mp="3agetqacdy23v" />
        </div>

        <div className={reversed ? 'lg:order-1' : ''} data-id="element-334" data-mp="3v2hzuoayj5nq">
          <p className={`flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wider ${a.text}`} data-id="element-335" data-mp="3v0yonc45rkst">
            <ModelIcon accent={volume.accent} className="h-4 w-4" data-id="element-336" data-mp="rm2meyrpuj7z" />
            Volumen {volume.number}
          </p>
          <h3 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-id="element-337" data-mp="mmpxm8hab22r">{volume.title}</h3>
          <p className={`mt-2 font-display text-xl font-bold ${a.text}`} data-id="element-338" data-mp="hn3n46subzxn">{volume.subtitle}</p>

          <div className="mt-6 space-y-3 text-lg leading-relaxed text-muted" data-id="element-339" data-mp="dnba9hwez7mh">
            {volume.intro.map(line => <p key={line} data-id="element-340" data-mp="32fxcs01ilfpz">{line}</p>)}
          </div>

          {volume.highlight && <p className="mt-4 font-display text-2xl font-black text-ink" data-id="element-341" data-mp="2ybzcvn4d80vv">{volume.highlight}</p>}

          {volume.flow && <ol className="mt-6 grid gap-3 sm:grid-cols-3" data-id="element-342" data-mp="2zupikgnbcw74">
              {volume.flow.map((step, i) => <li key={step} className={`rounded-xl ${a.soft} p-4`} data-id="element-343" data-mp="34qy4qdzxhpns" data-mpi={i}>
                  <span className={`font-display text-sm font-black ${a.text}`} data-id="element-344" data-mp="2pv8h6bf3h4gy">{i + 1}</span>
                  <p className="mt-1 font-semibold text-ink" data-id="element-345" data-mp="2utjpuitnl8xl">{step}</p>
                </li>)}
            </ol>}

          {volume.chips && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-346" data-mp="2luollca668wx">
              {volume.chips.map(chip => <li key={chip} className={`rounded-full ${a.soft} px-3 py-1 text-sm font-semibold ${a.text}`} data-id="element-347" data-mp="275sebhfxszxd">
                  {chip}
                </li>)}
            </ul>}

          {volume.contrast && <div className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-348" data-mp="1y5412oydb3lj">
              <div className="rounded-xl border border-line bg-surface p-4" data-id="element-349" data-mp="226p7brgecjmr">
                <p className="text-sm text-muted" data-id="element-350" data-mp="1jhvkf8mai626">Hay una diferencia enorme entre</p>
                <p className="mt-1 font-semibold text-muted line-through decoration-danger decoration-2" data-id="element-351" data-mp="1jjzobqk6v52g">
                  {volume.contrast.before}
                </p>
              </div>
              <div className={`rounded-xl ${a.soft} p-4`} data-id="element-352" data-mp="1sjuz3u9u0y1y">
                <p className="text-sm text-muted" data-id="element-353" data-mp="1nj79nd194p9u">y</p>
                <p className={`mt-1 font-display text-lg font-black ${a.text}`} data-id="element-354" data-mp="1nlthzevyv8t4">{volume.contrast.after}</p>
              </div>
            </div>}

          <p className="mt-10 font-semibold text-ink" data-id="element-355" data-mp="1xgv8ae1zagxa">{volume.learnLabel}</p>
          <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-356" data-mp="2b9ky6tkmo5zo">
            {visible.map(topic => <TopicItem key={topic} topic={topic} colorClass={a.text} data-id="element-357" data-mp="2777t87dpgc3y" />)}
          </ul>
          <AnimatePresence initial={false} data-id="element-358" data-mp="2l8aewj0sfkec">
            {expanded && <motion.ul initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid gap-x-8 gap-y-3 overflow-hidden pt-3 sm:grid-cols-2" data-id="element-359" data-mp="2g8ebakw05uzb">
                {hidden.map(topic => <TopicItem key={topic} topic={topic} colorClass={a.text} data-id="element-360" data-mp="3e1durghl70ov" />)}
              </motion.ul>}
          </AnimatePresence>
          {hidden.length > 0 && <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-4 py-2 text-sm font-semibold text-ink transition-colors duration-150 ease-out hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-ink" data-id="element-361" data-mp="2lcq4f6hzdl4g">
              {expanded ? 'Ver menos' : `Ver los ${volume.topics.length} temas`}
              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-362" data-mp="1v2pp6lop4e5r" />
            </button>}

          <div className={`mt-10 rounded-2xl ${a.bg} p-6 text-white sm:p-7`} data-id="element-363" data-mp="1lc4lwky2ulgi">
            <p className="text-white/80" data-id="element-364" data-mp="1gemlxk21fgkn">{volume.goalLabel}</p>
            <p className="mt-1 font-display text-2xl font-black leading-tight sm:text-3xl" data-id="element-365" data-mp="1gc8r9cb9s6dm">{volume.goal}</p>
          </div>
        </div>
      </div>
    </article>;
}
function TopicItem({
  topic,
  colorClass
}: {
  topic: string;
  colorClass: string;
}) {
  return <li className="flex gap-2.5 text-ink" data-id="element-366" data-mp="12f3ekth1w99e">
      <CheckIcon className={`mt-0.5 h-5 w-5 shrink-0 ${colorClass}`} aria-hidden="true" data-id="element-367" data-mp="12g4ylng1o639" />
      <span data-id="element-368" data-mp="xhldtqd6t0ch">{topic}</span>
    </li>;
}